import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import LinkCityScene from "@/apps/rider/components/LinkCityScene";

/**
 * /verify-email?token=… — landing page for the magic-link button in the
 * verification email. Three terminal states: success, error (expired/invalid),
 * already-verified.
 */
export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const token = sp.get("token");
  const [state, setState] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setErrorMsg("Missing verification token."); return; }
    let cancelled = false;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        if (!cancelled) {
          setState("success");
          // Refresh the user object so any signed-in session sees email_verified=true.
          refreshUser?.().catch(() => {});
        }
      } catch (e) {
        if (!cancelled) {
          setState("error");
          setErrorMsg(formatApiError(e.response?.data?.detail) || "Verification failed");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, refreshUser]);

  const resend = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification", {});
      toast.success("Verification email sent.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't resend right now");
    } finally { setResendBusy(false); }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#0A1128] text-[#F9F7F4]" data-testid="verify-email-page">
      <LinkCityScene />
      <div
        className="relative min-h-full flex flex-col items-stretch px-5 sm:px-8 max-w-md mx-auto"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 56px)",
          paddingBottom: "max(env(safe-area-inset-bottom), 32px)",
        }}
      >
        <div className="flex-1" aria-hidden="true" />
        <div className="bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-3xl p-7 shadow-2xl">
          {state === "loading" && (
            <div className="text-center py-6" data-testid="verify-email-loading">
              <Loader2 size={20} className="animate-spin text-[#ed6500] mx-auto" />
              <p className="text-[13px] text-[#F9F7F4]/65 mt-3">Verifying your email…</p>
            </div>
          )}

          {state === "success" && (
            <div className="text-center" data-testid="verify-email-success">
              <div className="w-14 h-14 mx-auto bg-[#ed6500]/15 text-[#ed6500] rounded-2xl inline-flex items-center justify-center">
                <CheckCircle2 size={22} />
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500] mt-4">
                Email verified
              </div>
              <h1 className="font-serif font-light text-[28px] leading-[1.1] mt-1">
                You're all set.
              </h1>
              <p className="text-[13.5px] text-[#F9F7F4]/65 mt-2">
                Ride confirmations, pickup alerts, and renewal receipts will land in your inbox going forward.
              </p>
              <button
                type="button"
                onClick={() => nav("/account")}
                className="mt-6 w-full bg-[#ed6500] hover:bg-[#ff8a36] text-white px-5 py-3 rounded-2xl text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2"
                data-testid="verify-email-continue"
              >
                Continue to dashboard <ArrowRight size={14} />
              </button>
            </div>
          )}

          {state === "error" && (
            <div className="text-center" data-testid="verify-email-error">
              <div className="w-14 h-14 mx-auto bg-[#ed6500]/15 text-[#ed6500] rounded-2xl inline-flex items-center justify-center">
                <AlertCircle size={22} />
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500] mt-4">
                Verification issue
              </div>
              <h1 className="font-serif font-light text-[28px] leading-[1.1] mt-1">
                This link can't be used.
              </h1>
              <p className="text-[13.5px] text-[#F9F7F4]/65 mt-2">{errorMsg}</p>
              <button
                type="button"
                onClick={resend}
                disabled={resendBusy}
                className="mt-6 w-full bg-[#ed6500] hover:bg-[#ff8a36] text-white px-5 py-3 rounded-2xl text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="verify-email-resend"
              >
                {resendBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                Send a new link
              </button>
              <button
                type="button"
                onClick={() => nav("/account")}
                className="mt-2 w-full text-[11px] tracking-[0.2em] uppercase font-semibold text-[#F9F7F4]/55 hover:text-[#ed6500] py-2"
              >
                Continue without verifying
              </button>
            </div>
          )}
        </div>
        <div className="flex-1" aria-hidden="true" />
      </div>
    </div>
  );
}
